import type { ValueOf } from '#layers/base/public';
import type { FieldFilter, Filter } from '#layers/data-model/types/filters';
import type { PrimitiveType } from '#layers/data-model/types/schema/primitive-type';
import type { ResolvedMonospaceCollection, ResolvedMonospacePrimitiveField } from '#layers/data-model/types/schema/resolved-schema';
import type { QueryCapability } from './query-capability';
import type { QuerySchemaRead } from './query-schema';
import { isUuid } from '#layers/base/public';
import { FilterOperator, LogicalOperator } from '#layers/data-model/types/filters';
import { parseInteger64Text } from './integer-64';
import { isPrimitiveTypeInteger64, isPrimitiveTypeNumber, isPrimitiveTypeString, isPrimitiveTypeUuid } from './primitive-type';
import { mapQueryCapability, queryCapabilityAllows } from './query-capability';

/** The operator a text search applies to a field of this type, if it searches the type at all. */
function getTextSearchOperator(type: PrimitiveType): typeof FilterOperator.ICONTAINS | typeof FilterOperator.EQ | undefined {
	if (isPrimitiveTypeString(type)) return FilterOperator.ICONTAINS;
	if (isPrimitiveTypeUuid(type) || isPrimitiveTypeNumber(type)) return FilterOperator.EQ;
}

/** The value a search term takes for a field of this type; `undefined` when the term cannot match it. */
function getTextSearchValue(type: PrimitiveType, search: string): unknown {
	if (isPrimitiveTypeString(type)) return search;
	if (isPrimitiveTypeUuid(type)) return isUuid(search) ? search : undefined;
	if (isPrimitiveTypeInteger64(type)) return parseInteger64Text(search, type.name);
	if (isPrimitiveTypeNumber(type)) return Number.isNaN(Number(search)) ? undefined : Number(search);
}

/** The clause a text search adds for a field; `undefined` when the field is not searched or the term cannot match it. */
export function getTextSearchClause(field: Pick<ResolvedMonospacePrimitiveField, 'apiName' | 'type'>, search: string): FieldFilter | undefined {
	const operator = getTextSearchOperator(field.type);
	const value = getTextSearchValue(field.type, search);
	return operator && value !== undefined ? { [field.apiName]: { [operator]: value } } as FieldFilter : undefined;
}

export const TextSearchResultKind = {
	/** None of the fields is searched by type, so the term is ignored. */
	Unsearchable: 'unsearchable',
	/** Fields are searched, but the term cannot match any of them, e.g. text against numeric fields. */
	NoMatch: 'noMatch',
	Filter: 'filter',
} as const;

export type TextSearchResultKind = ValueOf<typeof TextSearchResultKind>;

/** What a text search asks of a read. */
export type TextSearch =
	| { kind: typeof TextSearchResultKind.Unsearchable }
	| { kind: typeof TextSearchResultKind.NoMatch }
	| { kind: typeof TextSearchResultKind.Filter; filter: Filter };

/** Resolves a search term over `fields`, combining several matching fields with `_or`. */
export function resolveTextSearch(fields: Pick<ResolvedMonospacePrimitiveField, 'apiName' | 'type'>[], search: string): TextSearch {
	const searched = fields.filter((field) => getTextSearchOperator(field.type));
	if (searched.length === 0) return { kind: TextSearchResultKind.Unsearchable };

	const clauses = searched.flatMap((field) => getTextSearchClause(field, search) ?? []);
	if (clauses.length === 0) return { kind: TextSearchResultKind.NoMatch };

	return { kind: TextSearchResultKind.Filter, filter: clauses.length === 1 ? clauses[0]! : { _or: clauses } };
}

/**
 * The fields a text search may expand into: those whose search operator the read filter accepts.
 * Several fields combine with `_or`, so a read filter without it offers no search at all rather than
 * a partial one. Ungated collections search every supported primitive field.
 */
export function getTextSearchFields(collection: ResolvedMonospaceCollection, read: QueryCapability<QuerySchemaRead>): string[] {
	const filter = mapQueryCapability(read, (surface) => surface.filter());

	const fields = collection.primitiveFields.flatMap((field) => {
		const operator = getTextSearchOperator(field.type);
		const searchable = operator && queryCapabilityAllows(filter, (surface) => surface.primitiveFieldById(field.id)?.operators().includes(operator) === true);
		return searchable ? [field.apiName] : [];
	});

	return fields.length > 1 && !queryCapabilityAllows(filter, (surface) => surface.logical(LogicalOperator.OR)) ? [] : fields;
}
